import axiosInstance from 'shared/api/axiosInstance';

const BASE = '/system-register';

/** Build list query: default ACTIVE; showAll → ALL. */
export const listStatusParams = (showAll = false) => ({
  status: showAll ? 'ALL' : 'ACTIVE',
});

export const softwareApi = {
  list: (params) => axiosInstance.get(`${BASE}/software`, { params }),
  /** Softwares with a single nested collection hydrated (avoids N+1 detail GETs). */
  listNested: (params) => axiosInstance.get(`${BASE}/software/nested`, { params }),
  get: (id) => axiosInstance.get(`${BASE}/software/${id}`),
  registryRow: (id) => axiosInstance.get(`${BASE}/software/${id}/registry-row`),
  report: (id) => axiosInstance.get(`${BASE}/software/${id}/report`),
  create: (body) => axiosInstance.post(`${BASE}/software`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/software/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/software/${id}`),
  nextInternalReference: () => axiosInstance.get(`${BASE}/software/next-internal-reference`),
  /** Persist 1st/2nd approval for a deployment stage immediately. */
  recordDeploymentStageApproval: (id, body) =>
    axiosInstance.post(`${BASE}/software/${id}/deployment-stage-approvals`, body),
  /** Persist 1st/2nd approve/decline for a monitoring review immediately. */
  recordMonitoringReviewApproval: (softwareId, reviewId, body) =>
    axiosInstance.post(`${BASE}/software/${softwareId}/monitoring-reviews/${reviewId}/approvals`, body),
  /** Persist 1st/2nd approve/decline for an incident immediately. */
  recordIncidentApproval: (softwareId, incidentId, body) =>
    axiosInstance.post(`${BASE}/software/${softwareId}/incidents/${incidentId}/approvals`, body),
  /** Persist 1st/2nd approve/decline for a change-management record immediately. */
  recordChangeManagementApproval: (softwareId, changeId, body) =>
    axiosInstance.post(`${BASE}/software/${softwareId}/change-managements/${changeId}/approvals`, body),
};

export const businessUnitApi = {
  list: (params) => axiosInstance.get(`${BASE}/business-units`, { params }),
  create: (body) => axiosInstance.post(`${BASE}/business-units`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/business-units/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/business-units/${id}`),
};

export const businessProcessApi = {
  list: (params) => axiosInstance.get(`${BASE}/business-processes`, { params }),
  create: (body) => axiosInstance.post(`${BASE}/business-processes`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/business-processes/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/business-processes/${id}`),
  restore: (id) => axiosInstance.put(`${BASE}/business-processes/${id}/restore`),
};

export const systemOwnerApi = {
  list: (params) => axiosInstance.get(`${BASE}/system-owners`, { params }),
  create: (body) => axiosInstance.post(`${BASE}/system-owners`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/system-owners/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/system-owners/${id}`),
};

export const vendorApi = {
  list: (params) => axiosInstance.get(`${BASE}/vendors`, { params }),
  create: (body) => axiosInstance.post(`${BASE}/vendors`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/vendors/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/vendors/${id}`),
};

export const regulationApi = {
  list: (params) => axiosInstance.get(`${BASE}/regulations`, { params }),
  create: (body) => axiosInstance.post(`${BASE}/regulations`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/regulations/${id}`),
};

export const riskRegisterApi = {
  list: (params) => axiosInstance.get(`${BASE}/risk-register`, { params }),
  get: (id) => axiosInstance.get(`${BASE}/risk-register/${id}`),
  listSoftwareByRiskId: (id) => axiosInstance.get(`${BASE}/risk-register/${id}/software`),
  create: (body) => axiosInstance.post(`${BASE}/risk-register`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/risk-register/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/risk-register/${id}`),
};

export const assetRegisterApi = {
  list: (params) => axiosInstance.get(`${BASE}/asset-register`, { params }),
  /** Active IT assets assigned to the current user. */
  mine: () => axiosInstance.get(`${BASE}/asset-register/mine`),
  get: (id) => axiosInstance.get(`${BASE}/asset-register/${id}`),
  create: (body) => axiosInstance.post(`${BASE}/asset-register`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/asset-register/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/asset-register/${id}`),
  nextAssetId: () => axiosInstance.get(`${BASE}/asset-register/next-asset-id`),
};

/** Standalone incidents (software and/or IT assets via ITAssetIncidents). */
export const accessProvisionApi = {
  list: (params) => axiosInstance.get(`${BASE}/access-provisions`, { params }),
  listApprovals: (params) => axiosInstance.get(`${BASE}/access-provisions/approvals`, { params }),
  mine: () => axiosInstance.get(`${BASE}/access-provisions/mine`),
  get: (id) => axiosInstance.get(`${BASE}/access-provisions/${id}`),
  create: (body) => axiosInstance.post(`${BASE}/access-provisions`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/access-provisions/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/access-provisions/${id}`),
  recordApproval: (id, body) => axiosInstance.post(`${BASE}/access-provisions/${id}/approvals`, body),
};

export const incidentApi = {
  list: (params) => axiosInstance.get(`${BASE}/incidents`, { params }),
  get: (id) => axiosInstance.get(`${BASE}/incidents/${id}`),
  create: (body) => axiosInstance.post(`${BASE}/incidents`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/incidents/${id}`, body),
  remove: (id) => axiosInstance.put(`${BASE}/incidents/${id}/status`, { status: 'DELETED' }),
  restore: (id) => axiosInstance.put(`${BASE}/incidents/${id}/restore`),
  recordApproval: (id, body) => axiosInstance.post(`${BASE}/incidents/${id}/approvals`, body),
};

export const monitoringReviewApi = {
  list: (params) => axiosInstance.get(`${BASE}/monitoring-reviews`, { params }),
  get: (id) => axiosInstance.get(`${BASE}/monitoring-reviews/${id}`),
  create: (body) => axiosInstance.post(`${BASE}/monitoring-reviews`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/monitoring-reviews/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/monitoring-reviews/${id}`),
};

export const sopDocumentApi = {
  list: (params) => axiosInstance.get(`${BASE}/sop/documents`, { params }),
  get: (id) => axiosInstance.get(`${BASE}/sop/documents/${id}`),
  create: (body) => axiosInstance.post(`${BASE}/sop/documents`, body),
  update: (id, body) => axiosInstance.put(`${BASE}/sop/documents/${id}`, body),
  remove: (id) => axiosInstance.delete(`${BASE}/sop/documents/${id}`),
  restore: (id) => axiosInstance.put(`${BASE}/sop/documents/${id}/restore`),
};

export const systemDocumentApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosInstance.post(`${BASE}/documents/upload`, formData);
  },
  /** Fetch stored file as blob (auth required). `location` is API path from upload response. */
  fetchFile: (location) => {
    const path = locationToAxiosRelativePath(location);
    if (!path) {
      return Promise.reject(new Error('Invalid document location'));
    }
    return axiosInstance.get(path, { responseType: 'blob' });
  },
};

function locationToAxiosRelativePath(location) {
  if (!location || !String(location).trim()) return null;
  let path = String(location).trim();
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }
  if (!path.startsWith('/')) path = `/${path}`;
  const prefix = '/api/v1/';
  if (path.toLowerCase().startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (path.startsWith('/system-register/')) {
    return path.slice(1);
  }
  return path.startsWith('/') ? path.slice(1) : path;
}

/** @deprecated use named exports above */
export const systemRegisterLookupApi = {
  listBusinessUnits: businessUnitApi.list,
  createBusinessUnit: businessUnitApi.create,
  listSystemOwners: systemOwnerApi.list,
  createSystemOwner: systemOwnerApi.create,
  listVendors: vendorApi.list,
  createVendor: vendorApi.create,
  listRegulations: regulationApi.list,
};
